<template>
  <BaseCard
    :loading="loading"
    :minLoadingHeight="400"
    :heading="$t('insights.occupancy')"
  >
    <Flex
      direction="column"
      :gap="2"
    >
      <Flex :gap="2">
        <Flex
          direction="column"
          :gap=".5"
        >
          <HeadingLegend
            :text="$t('insights.time_booked')"
            :color="colors.booked"
          />
          <BaseText
            size="l"
            v-test="'insights-occupancy-time-booked'"
          >
            {{ currentPeriod.data.scheduledHours }}
            {{ $t('global.items.hour', currentPeriod.data.scheduledHours) }}
          </BaseText>
        </Flex>
        <Flex
          direction="column"
          :gap=".5"
        >
          <HeadingLegend
            :text="$t('insights.time_available')"
            :color="colors.available"
          />
          <BaseText
            size="l"
            v-test="'insights-occupancy-time-available'"
          >
            {{ currentPeriod.data.availableHours }}
            {{ $t('global.items.hour', currentPeriod.data.availableHours) }}
          </BaseText>
        </Flex>
      </Flex>
      <Chart :data="allData" />
    </Flex>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import dayjs from '@/dayjs';
import { useLocationsStore } from '@/stores/locations';
import HeadingLegend from '../HeadingLegend.vue';
import Chart from './Chart.vue';
import { useColors } from './colors';
import { GET_AVAILABILITY_INSIGHTS } from './graphql';

const emit = defineEmits(['loaded']);

const colors = useColors();

const { locationId, dataScope } = storeToRefs(useLocationsStore());

const startAt = inject<Ref<string>>('startAt')!;
const endAt = inject<Ref<string>>('endAt')!;

const periods = computed(() =>
  Array.from({ length: 4 }, (_, i) => ({
    startAt: dayjs(startAt.value).subtract(i, 'week').format('YYYY-MM-DD'),
    endAt: dayjs(endAt.value).subtract(i, 'week').format('YYYY-MM-DD'),
  })).reverse(),
);

const queries = ref<any>([]);
watch(
  periods,
  () => {
    queries.value = periods.value.map(({ startAt, endAt }) =>
      useQuery(GET_AVAILABILITY_INSIGHTS, () => ({
        dataScope: dataScope.value,
        locationId: locationId.value,
        startAt,
        endAt,
      })),
    );
  },
  { immediate: true },
);

const loading = computed(() => queries.value.some((q) => q.loading));

const allData = computed(() =>
  periods.value.map((period, index) => {
    const data = queries.value[index]?.result?.availabilityInsights || null;
    return {
      ...period,
      data: {
        scheduledHours: Math.round((data?.scheduledTime / 60) * 100) / 100 || 0,
        availableHours: Math.round((data?.availableTime / 60) * 100) / 100 || 0,
      },
    };
  }),
);

const currentPeriod = computed(() => allData.value[allData.value.length - 1]);

watch(currentPeriod, (currentPeriod) => {
  if (!loading.value) {
    emit('loaded', {
      scheduledHours: currentPeriod.data.scheduledHours,
      availableHours: currentPeriod.data.availableHours,
    });
  }
});
</script>
